/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';

import AppUtils from '../../../utilities/AppUtils';
import RouteNames from '../../appAuthorized/RouteNames';

import ContentLoader from '../../components/ContentLoader';

import { useProfile, useActiveProfile, useAllProfiles, useNextProfileColor } from '../../hooks/useProfile';
import { useUser, useActiveUser } from '../../hooks/useUser';
import { useUIState } from '../../hooks/useUIState';
import { deepLink, activeProfile } from '../../../utilities/LocalStorage';
import { useLists } from '../../hooks/useLists';
import { useNotificationInterval } from '../../hooks/useNotifications';
import { usePolicy } from '../../hooks/usePolicy';
import { UserMetricsProfile } from '../../../utilities/UserMetrics';
import CustomerService from '../../../utilities/CustomerService';
import { useProfileActionItems, useUserActionItems } from '../../hooks/useActionItems';
import { ActionItemViewName } from '../../../models/ActionItemModel';
import UserModel from '../../../models/UserModel';
import { useGroupsForProfile } from '../../hooks/useGroup';
import { useAndroidInitialization } from '../../hooks/useAndroid';

function useRequiredDataQuery() {
  const [{ user, preferredLocale }] = useActiveUser();

  const userProfileId = user.profile_id;

  const [{ greetingName }] = useProfile(userProfileId, false);

  useAllProfiles();
  useNextProfileColor();

  const [, uiActions] = useUIState();
  const [listData] = useLists();
  const [notifications] = useNotificationInterval();
  const [policyData] = usePolicy(true);
  const requiredDataLoaded = listData.isLoaded && notifications.isLoaded && policyData.isLoaded;
  const [, groupActions] = useGroupsForProfile(userProfileId);

  React.useEffect(() => {
    if (user.email) {
      UserMetricsProfile.init(user.cuid, user.created_at);

      CustomerService.initUser(
        user.email,
        greetingName,
        user.cuid,
        user.intercom_web_hash,
        preferredLocale.language_code,
      );

      groupActions.fetch();
      uiActions.setUser(user.id, user.profile_id);
    }
  }, [user.email]);

  return {
    isLoaded: requiredDataLoaded,
  };
}
function determineRedirectProps(redirectUrl, destinationUrl) {
  return {
    pathname: redirectUrl,
    state: {
      destinationUrl: destinationUrl === redirectUrl ? null : destinationUrl,
    },
  };
}

function handleRedirect(redirectRoute, currentRoute, props) {
  if (currentRoute === redirectRoute) {
    return props.children;
  } else {
    return <Redirect to={determineRedirectProps(redirectRoute, currentRoute)} />;
  }
}

const AppInitialization = (props) => {
  const { userId } = props;
  const [, uiStateActions] = useUIState();
  const [{ user, ...userData }] = useUser(userId);
  const userProfileId = user.profile_id;
  const [userProfileData, userProfileActions] = useProfile(userProfileId, true);
  const initialProfileId = activeProfile.get() || user.profile_id;
  const activeProfileisDependent = initialProfileId !== userProfileId;
  const [activeProfileData] = useActiveProfile(activeProfileisDependent);
  const [initialized, setInitialized] = React.useState(false);
  const requiredDataQuery = useRequiredDataQuery();
  const [userActionItems] = useUserActionItems(props.userId);
  const [profileActionItems] = useProfileActionItems(user.profile_id);

  const [isAppMounted, setAppMounted] = React.useState(false);

  const userLoaded = userData.isLoaded;
  const userProfileLoaded = userProfileData.isLoaded;
  const activeProfileLoaded = activeProfileData.isLoaded;

  const userProfile = userProfileActions.instantiateProfile();
  const initialDestinationUrl = window.location.pathname;
  const hasUserActionItems = userActionItems.actionItemList.length > 0;
  const requiresNewUserSetup = hasUserActionItems && !userProfile.hasPreferredName();

  const actionItemsLoaded = userActionItems.isLoaded && profileActionItems.isLoaded;
  const isDestinationLink = initialDestinationUrl.indexOf('/redirect/') === 0;
  const hasProfileActionItems = profileActionItems.actionItemList.length > 0;
  const requiresNewProfileSetup = hasProfileActionItems && profileActionItems.actionItemList.some(a => a.view === ActionItemViewName.profile_name);
  const hasJoinGroupIntent = UserModel.hasGroupIntent(user);
  const isTokenView = initialDestinationUrl.indexOf('token/') > -1;

  React.useEffect(() => {
    if (userData.error) {
      AppUtils.logout({}, AppUtils.reloadApp);
    } else if (userData.isLoaded) {
      window.BPH = window.BPH || {};
      window.BPH.CORDOVA_LOGGED_IN = true;
    }
  }, [userLoaded]);

  React.useEffect(() => {
    if (userProfileData.error) {
      AppUtils.logout({}, AppUtils.reloadApp);
    } else if (userProfileLoaded && user.email) {
      uiStateActions.setActiveProfile(initialProfileId);
    }
  }, [userProfileLoaded, user.email]);

  React.useEffect(() => {
    if (activeProfileLoaded) {
      uiStateActions.setActiveColorTheme({ id: activeProfileData.data.color_theme_id });
      setInitialized(true);
    }
  }, [activeProfileLoaded]);

  useAndroidInitialization(userId);

  const dataMissing = !actionItemsLoaded || !requiredDataQuery.isLoaded || !userLoaded || !userProfileLoaded || !initialized;

  React.useEffect(() => {
    if (!dataMissing) {
      setAppMounted(true);
    }
  }, [dataMissing]);

  if (dataMissing) {
    return <ContentLoader isContentLoaded={false} isFixed={true} />;
  } else if (window.backpackRoute) {
    const route = window.backpackRoute;
    window.backpackRoute = null;
    return <Redirect to={route} />;
  } else if (isAppMounted) {
    return props.children;
  } else if (isTokenView) {
    return props.children;
  } else if (requiresNewUserSetup) {
    return handleRedirect(RouteNames.newUserSetup(), initialDestinationUrl, props);
  } else if (hasUserActionItems) {
    return handleRedirect(RouteNames.userSetup(), initialDestinationUrl, props);
  } else if (isDestinationLink) {
    return props.children;
  } else if (requiresNewProfileSetup) {
    return handleRedirect(RouteNames.newProfileSetup(), initialDestinationUrl, props);
  } else if (hasProfileActionItems) {
    return handleRedirect(RouteNames.profileSetup(), initialDestinationUrl, props);
  } else if (hasJoinGroupIntent) {
    return handleRedirect(RouteNames.intendedGroup(), initialDestinationUrl, props);
  } else if (deepLink.get()) {
    const route = deepLink.get();
    deepLink.set('');
    return <Redirect to={route} />;
  } else {
    return props.children;
  }
};

export default withRouter(AppInitialization);
